import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Loader2, ArrowLeft } from "lucide-react";
import { getPaymentStatus } from "../lib/payments";

const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("failed"); return; }
    if (attempt >= MAX_ATTEMPTS) { setState("timeout"); return; }
    try {
      const data = await getPaymentStatus(sessionId);
      setInfo(data);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.status === "expired") { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  const CONFIG = {
    polling: { icon: Loader2, spin: true, color: "text-primary", title: "Confirming Payment", body: "Please hold while we verify your transaction with Stripe..." },
    paid: { icon: Check, color: "text-accent", title: "Payment Successful", body: "Thank you. A receipt has been sent and our team will follow up on your service shortly." },
    failed: { icon: X, color: "text-destructive", title: "Payment Not Completed", body: "Your payment was cancelled or could not be verified. You have not been charged." },
    timeout: { icon: Loader2, color: "text-muted-foreground", title: "Still Processing", body: "This is taking longer than expected. Check your email for confirmation, or contact us." },
  };
  const c = CONFIG[state];
  const Icon = c.icon;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="grain-overlay" />
      <div className="max-w-[1200px] w-full mx-auto px-5 md:px-10 py-8">
        <button onClick={() => nav("/")} className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft size={14} /> Back to site
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-20">
        <motion.div
          data-testid="payment-result"
          data-status={state}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg border border-border bg-card p-10 md:p-14"
        >
          <div className={`w-16 h-16 border border-border flex items-center justify-center mb-8 ${c.color}`}>
            <Icon size={30} className={c.spin ? "animate-spin" : ""} />
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter uppercase mb-4">{c.title}</h1>
          <p className="font-mono text-sm text-muted-foreground leading-relaxed">{c.body}</p>
          {info && state === "paid" && (
            <div className="mt-8 border-t border-border pt-6 font-mono text-sm">
              <div className="flex justify-between py-1"><span className="text-muted-foreground">Item</span><span>{info.label || "Payment"}</span></div>
              <div className="flex justify-between py-1"><span className="text-muted-foreground">Amount</span><span className="tabular-nums">${Number(info.amount).toFixed(2)}</span></div>
            </div>
          )}
          {state !== "polling" && (
            <div className="mt-10 flex gap-3">
              <button onClick={() => nav("/")} className="bg-primary text-primary-foreground font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-primary hover:bg-transparent hover:text-primary transition-colors duration-300">Home</button>
              {state !== "paid" && (
                <button onClick={() => nav("/pay")} className="font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-border hover:border-primary hover:text-primary transition-colors duration-300">Try Again</button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
